import type { OnboardingStatus } from "@/services/onboardingService";

/**
 * In-memory cache for `GET /onboarding/status/` (AITS-590).
 *
 * The app-shell onboarding gate used to refetch this on every route change and
 * blank the whole app while it waited, so dashboard → Settings → Workbench
 * flashed a full-screen blocker three times. The gate is UX routing, not
 * enforcement — the backend refuses the actual operations independently with
 * `402 billing_onboarding_required` (reports/views.py, workspaces/views.py) —
 * so it can safely read a cached answer and re-evaluate its (path-dependent)
 * rule locally.
 *
 * Lives outside React, in its own module, for two reasons:
 *  - every caller of the service writes through it, so the OnboardingPage's own
 *    10s poll and its PATCH responses keep the gate's copy hot for free. That
 *    matters: OnboardingPage navigates to `/` the moment it sees `complete`,
 *    and a gate holding a stale "incomplete" would bounce it straight back.
 *  - `api.ts` can invalidate it from the response interceptor without importing
 *    the service (which imports `api`) and creating a cycle. Only the *type* is
 *    imported here, and that is erased at build time.
 */

/**
 * How long a cached status stays authoritative.
 *
 * Nothing the user does in-app changes their onboarding state without going
 * through this service (which writes through), so this TTL only covers changes
 * made *elsewhere*: another tab, a Stripe webhook settling, or the nightly
 * downgrade re-deferring a guest. All of those are rare and none is protected
 * by this gate.
 *
 * Expiry is checked when the gate runs, i.e. on navigation — so a user parked
 * on one screen is not revalidated on a timer. That is deliberate: the gate
 * only decides where to *send* someone, so it has nothing to do until they
 * move, and a background timer would be work with no observable effect.
 */
export const ONBOARDING_STATUS_TTL_MS = 5 * 60 * 1000;

export interface OnboardingStatusEntry {
  status: OnboardingStatus;
  /** `Date.now()` at the time the response landed. */
  fetchedAt: number;
}

let entry: OnboardingStatusEntry | null = null;
const listeners = new Set<() => void>();

/**
 * Bumped on every invalidation, so a response that was already in flight when
 * the invalidation happened can be dropped instead of written through.
 *
 * Without this, the 402 path has a race it cannot win: a `/onboarding/status/`
 * GET issued a moment earlier carries the state from *before*
 * `activate_deferred_onboarding()` ran, and landing it after the clear would
 * restore exactly the stale answer the clear existed to remove.
 */
let generation = 0;

export const currentOnboardingStatusGeneration = (): number => generation;

const emit = (): void => {
  listeners.forEach((listener) => listener());
};

/**
 * The payload is a flat JSON object from one serializer, so field-order is
 * stable and this is a sound equality check. Only an optimisation: a false
 * "changed" costs one render, never correctness.
 */
const sameStatus = (a: OnboardingStatus, b: OnboardingStatus): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** Snapshot for `useSyncExternalStore`. Identity is stable until replaced. */
export const readOnboardingStatusEntry = (): OnboardingStatusEntry | null => entry;

export const subscribeToOnboardingStatus = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const isOnboardingStatusFresh = (
  candidate: OnboardingStatusEntry | null,
  now: number = Date.now(),
): boolean => candidate != null && now - candidate.fetchedAt < ONBOARDING_STATUS_TTL_MS;

/**
 * Write-through, called by every service function that receives a status.
 *
 * `startedAtGeneration` is the generation the request was issued under; a
 * response from before an invalidation is discarded rather than cached.
 *
 * An unchanged status refreshes the timestamp **in place and does not notify**.
 * The snapshot identity is what `useSyncExternalStore` diffs, so emitting a new
 * object for an identical status re-renders the app shell — and with it the
 * whole routed subtree under `<Outlet/>`. That is the same trap AITS-540 hit
 * when the report poll moved into the shell, and the pollers here (the
 * onboarding page every 10s, the billing panel on mount) are exactly the
 * callers that would spring it.
 */
export const cacheOnboardingStatus = (
  status: OnboardingStatus,
  startedAtGeneration: number = generation,
): void => {
  if (startedAtGeneration !== generation) return;

  if (entry !== null && sameStatus(entry.status, status)) {
    entry.fetchedAt = Date.now();
    return;
  }

  entry = { status, fetchedAt: Date.now() };
  emit();
};

/**
 * Mark the cached status as no longer authoritative, without discarding it.
 *
 * Called whenever the API answers `402 billing_onboarding_required`. That is
 * not just a hint that our copy might be stale: those backend paths call
 * `activate_deferred_onboarding()` before rejecting, so the 402 itself
 * *changes* the state we hold.
 *
 * Deliberately keeps the value. Nulling it would send the gate back to
 * `resolving` and swap the screen the user is currently on for a skeleton —
 * a mid-session interstitial, which is the exact thing this work removed. The
 * stale value keeps the page rendered; the failed freshness check forces an
 * immediate background refetch, and the redirect (if any) follows from that.
 */
export const invalidateOnboardingStatus = (): void => {
  generation += 1;
  if (entry === null) return;
  entry = { ...entry, fetchedAt: 0 };
  emit();
};

/**
 * Drop the cached status entirely.
 *
 * Sign-in/sign-out only, where there is no "current user" whose screen we
 * would be interrupting — and where keeping one account's state into another's
 * session would be wrong rather than merely stale.
 */
export const clearOnboardingStatusCache = (): void => {
  generation += 1;
  if (entry === null) return;
  entry = null;
  emit();
};
